import type {SpacingOptions} from '@github-ui/code-mirror'
import {getLanguageInfo} from '@github-ui/copilot-chat-helpers/language-info'
import {useMemo} from 'react'

const DEFAULT_TAB_SIZE = 4
const DEFAULT_SOFT_TAB_SIZE = 2
const MIN_TAB_SIZE = 1
const MAX_TAB_SIZE = 12

// ported from https://github.com/github/github/blob/master/app/helpers/editor_config_helper.rb
export function guessCodeMirrorSettings({language, value}: {language: string; value: string}): SpacingOptions {
  const indentStyle = guessIndentStyle(value)
  const indentSize = guessIndentSize(value, indentStyle)
  const lineWrapping = wrapMode(language)

  return {
    indentUnit: indentSize,
    indentWithTabs: indentStyle === 'tab',
    lineWrapping,
  }
}

export function useGuessCodeMirrorSettings({language, value}: {language: string; value: string}): SpacingOptions {
  const settings = useMemo(() => {
    return guessCodeMirrorSettings({language, value})
  }, [language, value])

  // we use this two step memoization so that even when the file changes, if the settings don't change, we return the same object
  return useMemo(
    () => ({
      indentUnit: settings.indentUnit,
      indentWithTabs: settings.indentWithTabs,
      lineWrapping: settings.lineWrapping,
    }),
    // it's important to deconstruct settings in the dependencies so that we don't unnecessarily return a new object
    [settings.indentUnit, settings.indentWithTabs, settings.lineWrapping],
  )
}

function guessIndentStyle(value: string) {
  return /^\t/m.test(value) ? 'tab' : 'space'
}

function castTabWidth(n: number | undefined) {
  return n !== undefined && n >= MIN_TAB_SIZE && n <= MAX_TAB_SIZE ? n : DEFAULT_SOFT_TAB_SIZE
}

function guessIndentSize(value: string, indentMode: 'tab' | 'space') {
  if (indentMode === 'space') {
    const match = value.match(/^( +)[^*]/im)
    return castTabWidth(match?.[1]?.length)
  } else {
    return DEFAULT_TAB_SIZE
  }
}

function wrapMode(language: string) {
  const info = getLanguageInfo(language)
  if (info?.wrap !== undefined) {
    return info.wrap
  }

  return true
}
